import { useCallback, useEffect, useMemo, useState } from "react";
import { Container } from "./styled";
import { Item__Atividades, ItemPropriedades } from "../../../../../../services/atividades/GET";
import { faAreaChart, faFileExcel, faMapLocation, faPrint, faSquare } from "@fortawesome/free-solid-svg-icons";
import { PopSelect } from "../../../../../../Components/CustomUi/PopSelect";
import { Switcher } from "../../../../../../Components/CustomUi/Switcher";
import Footer from "../../../../../../Components/Footer";
import ButtonCustom from "../../../../../../Components/ButtonCustom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "antd";
import { useAccess } from "../../../../../../context/useAccess";
import { subareasDoTalhao, talhoesDaPropriedade } from "./meta/lista-filtros";
import { errorAlert } from "../../../../../../utils/alert";
import { estruturaAtvsExecutadas } from "./meta/estrutura_todas_atvs";
import { useGetEquipeCliente } from "../../../../../../services/Equipe/GET";
import { gerarPdfAtvsExecutadasUrl } from "./meta/gerarPDFAtvsExecutadas";
import { gerarExcelAtvsExecutadas } from "./meta/gerarExcelAtvsExecutadas";

//PROPS
interface Props {
    data_propriedades: ItemPropriedades[];
    data_atividades: Item__Atividades[];
}

export const AtvsExecutadas: React.FC<Props> = ({ data_propriedades, data_atividades }) => {

    //CONS
    const { consultoria, nameCliente } = useAccess();

    //STATES
    const [cod_propriedade_selecionada, setCod_propriedade_selecionada] = useState<number>(null);
    const [nome_propriedade_selecionada, setNome_propriedade_selecionada] = useState<string>('');
    const [cod_talhao_selecionado, setCod_talhao_selecionado] = useState<number>(null);
    const [nome_talhao_selecionado, setNome_talhao_selecionado] = useState<string>('');
    const [cod_subarea_selecionada, setCod_subarea_selecionada] = useState<number>(null);
    const [nome_subarea_selecionada, setNome_subarea_selecionada] = useState<string>('');
    // const [incluir_insumos, setIncluir_insumos] = useState<boolean>(false);
    // const [incluir_patrimonios, setIncluir_patrimonios] = useState<boolean>(false);

    //API
    const { data_equipe_cliente } = useGetEquipeCliente(); //lista equipe

    // PDF Modal states
    const [modalPdfOpen, setModalPdfOpen] = useState(false);
    const [pdfDataUrl, setPdfDataUrl] = useState<string | null>(null);
    const [gerandoPdf, setGerandoPdf] = useState(false);
    const [gerandoExcel, setGerandoExcel] = useState(false);

    useEffect(() => {
        setCod_talhao_selecionado(null);
        setNome_talhao_selecionado('');
        setCod_subarea_selecionada(null);
        setNome_subarea_selecionada('');
    }, [cod_propriedade_selecionada]);

    useEffect(() => {
        setCod_subarea_selecionada(null);
        setNome_subarea_selecionada('');
    }, [cod_talhao_selecionado]);

    const gerarPDF = useCallback(async () => {
        setGerandoPdf(true);
        try {
            const estrutura_execucoes = estruturaAtvsExecutadas(data_atividades, data_propriedades, data_equipe_cliente, cod_propriedade_selecionada, cod_talhao_selecionado, cod_subarea_selecionada);

            const headerInfo = {
                titulo: "Relatório de Atividades Executadas",
                razaoSocial: `${consultoria?.razao_social} - ${consultoria?.linha1}`,
                logo: consultoria?.logo_url || "",
                cliente: nameCliente || ""
            };

            const dataUrl = await gerarPdfAtvsExecutadasUrl(estrutura_execucoes, headerInfo);
            setPdfDataUrl(dataUrl);
            setModalPdfOpen(true);

        } catch (err) {
            console.error(err);
            errorAlert("Erro ao gerar PDF.");
        } finally {
            setGerandoPdf(false);
        }
    }, [data_atividades, data_propriedades, data_equipe_cliente, consultoria, nameCliente, cod_propriedade_selecionada, cod_talhao_selecionado, cod_subarea_selecionada]);

    const gerarExcel = useCallback(async () => {
        setGerandoExcel(true);
        try {
            await new Promise((resolve) => setTimeout(resolve, 0));

            const estrutura_execucoes = estruturaAtvsExecutadas(data_atividades, data_propriedades, data_equipe_cliente, cod_propriedade_selecionada, cod_talhao_selecionado, cod_subarea_selecionada);

            const headerInfo = {
                titulo: "Relatório de Atividades Executadas",
                razaoSocial: `${consultoria?.razao_social} - ${consultoria?.linha1}`,
                logo: consultoria?.logo_url || "",
                cliente: nameCliente || ""
            };

            await gerarExcelAtvsExecutadas(estrutura_execucoes, headerInfo);

        } catch (err) {
            console.error(err);
            errorAlert("Erro ao gerar Excel.");
        } finally {
            setGerandoExcel(false);
        }
    }, [data_atividades, data_propriedades, data_equipe_cliente, consultoria, nameCliente, cod_propriedade_selecionada, cod_talhao_selecionado, cod_subarea_selecionada]);

    const lista_talhoes = useMemo(
        () => talhoesDaPropriedade(cod_propriedade_selecionada, data_propriedades),
        [cod_propriedade_selecionada, data_propriedades]
    );

    const lista_subareas = useMemo(
        () => subareasDoTalhao(cod_propriedade_selecionada, cod_talhao_selecionado, data_propriedades),
        [cod_propriedade_selecionada, cod_talhao_selecionado, data_propriedades]
    );

    return (
        <Container style={{ minHeight: 'calc(100vh - 130px)' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
                <PopSelect
                    label="Propriedade"
                    empty_placeholder="Selecione a propriedade"
                    value={nome_propriedade_selecionada}
                    onChange={setNome_propriedade_selecionada}
                    onChangeId={setCod_propriedade_selecionada}
                    items={data_propriedades}
                    descricaoItem="nome"
                    idItem="cod"
                    icon={faMapLocation}
                />
                <PopSelect
                    label="Talhão"
                    empty_placeholder="Selecione o talhão"
                    value={nome_talhao_selecionado}
                    onChange={setNome_talhao_selecionado}
                    onChangeId={setCod_talhao_selecionado}
                    items={lista_talhoes}
                    descricaoItem="nome"
                    idItem="cod"
                    icon={faAreaChart}
                    disabled={!cod_propriedade_selecionada}
                    show_svg={lista_talhoes.length < 7}
                />
                <PopSelect
                    label="Subárea"
                    empty_placeholder={lista_talhoes.length === 0 ? "Nenhuma subárea disponível" : "Selecione a subárea"}
                    value={nome_subarea_selecionada}
                    onChange={setNome_subarea_selecionada}
                    onChangeId={setCod_subarea_selecionada}
                    items={lista_subareas ?? []}
                    descricaoItem="nome"
                    idItem="cod"
                    icon={faSquare}
                    disabled={!cod_talhao_selecionado || lista_subareas.length === 0}
                />

                {/* <Switcher
                    isChecked={incluir_insumos}
                    trigger={setIncluir_insumos}
                    label="Incluir Insumos"
                />
                <Switcher
                    isChecked={incluir_patrimonios}
                    trigger={setIncluir_patrimonios}
                    label="Incluir Patrimônios"
                /> */}
            </div>

            <Footer style={{ justifyContent: 'flex-start', gap: 10, width: '100%' }}>
                <ButtonCustom
                    icon={<FontAwesomeIcon icon={faFileExcel} />}
                    onClick={gerarExcel}
                    loading={gerandoExcel}
                >
                    Gerar Excel
                </ButtonCustom>
                <ButtonCustom
                    icon={<FontAwesomeIcon icon={faPrint} />}
                    onClick={gerarPDF}
                    type="secondary"
                    loading={gerandoPdf}
                >
                    Gerar PDF
                </ButtonCustom>
            </Footer>

            <Modal
                title="Visualizar PDF do relatório"
                open={modalPdfOpen}
                onCancel={() => { setPdfDataUrl(null); setModalPdfOpen(false) }}
                footer={null}
                width="80%"
                style={{ top: 20 }}
                destroyOnClose
            >
                {pdfDataUrl ? (
                    <iframe
                        src={pdfDataUrl}
                        width="100%"
                        height="700px"
                        style={{ border: 'none' }}
                        title="PDF Viewer"
                    />
                ) : (
                    <p>Carregando PDF...</p>
                )}
            </Modal>
        </Container>
    );
};